/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React, { useContext, useEffect, useState } from 'react';
import {
    object, string, bool, array, func, shape, arrayOf, number,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import PDPAddToCartComponent from './v1/PDPAddToCart';
import PDPAddToCartComponentV2 from './v2/PDPAddToCart';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useSalesforcePersonalizedContentQuery from '../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import { getCurrentPageType } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getBrand } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getCaCampaign, getCardIsleData, getSelectedInlineAddons } from '../../../../../../state/ducks/App/App-Selectors';
import CaPriceVariant from '../../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/DesktopSimpleProduct/CaPriceTestType';
import { getCartOrder, getPassportSubscriptionStatus } from '../../../../../../state/ducks/Common/Common-Selectors';

const PDPAddToCart = ({
    product,
    product: {
        availability,
        productSkus,
        brandId,
        partNumber,
        name,
        displayDeliveryCalendar,
    },
    deliveryData,
    selectedSku,
    sortedData,
    presentation_family,
    subscriptionDetails,
    subscription,
    location,
    isPassportBundleItem,
    disableAddToCart,
    personalizedData,
    isAddToCartEnabled,
    hasWine,
    selectedAddons,
    handleMiniCartModalClick,
    quantity,
    isPassportBundleProduct,
    handleMobileAgeVerification,
    deviceType,
    deliverySLA,
    isStandAloneMovieSku,
    deliveryIndicator,
    productNotAvailable,
    pmallSummaryInfo,
    movieSelected,
    validVaseConfiguratorSku,
    content,
    isPdpRedesignEnabled,
    showAddToCart,
    showDeliveryField,
    showProductTitleTop,
    isPdpRedesignShortEnabled,
    isPdpRedesignSkuEnabled,
    addToCartButtonRedesign,
    isPlaRedesignEnabled,
    setProductQuantity,
    stickyAtcEnabled,
    stickyAtcRequiresSavedZip,
}) => {
    const { featureFlags = {} } = useContext(CrossMerch);
    const dispatch = useDispatch();
    const [isOffScreen, setIsOffScreen] = useState(false);
    const [totalAddonPrice, setTotalAddonPrice] = useState(0);
    const [passportPrice, setPassportPrice] = useState(0);
    const brand = useSelector(getBrand);

    // Adding this feature for brand specific for eg. enabling new pmall ATC for HD only
    const ffCurrentBrandPmallAtcOnSave = useSelector(getFeatureFlag('is-pmall-product-save-atc-enabled'));
    const pageType = useSelector(getCurrentPageType);
    const cardIsleData = useSelector(getCardIsleData);
    const isPmallPersonalizationAttr = (selectedSku?.attributes || [])?.find((attr) => attr.name === 'is_pmall_personalization_enabled');
    const isPmallPersonalizationEnabled = isPmallPersonalizationAttr?.values?.[0]?.value === 'true';
    const isPmallPersonalizationTypeAttr = (selectedSku?.attributes || [])?.find((attr) => attr.name === 'personalization_type_enabled');
    const pmallPersonalizationType = !!isPmallPersonalizationTypeAttr?.values?.[0]?.value;
    const ffGFAndGnPersistAddressCo = useSelector(getFeatureFlag('is-gf-and-gn-persist-address-checkout'));
    const ffCardIsleCurrentPrice = useSelector(getFeatureFlag('is-card-isle-current-card-price'));
    const forceGFAndGnPersistAddressCo = useSelector(getFeatureFlag('is-force-gf-and-gn-persist-address-checkout'));
    const isCaTransparencyPricing = useSelector(getFeatureFlag(['is-ca-transparency-all-in-pricing-enabled']));
    const cardIslePrice = Object.keys(cardIsleData)?.length > 0 ? ffCardIsleCurrentPrice : 0;
    const [skuDynamicPrice, setSkuDynamicPrice] = useState({});
    const caCampaign = useSelector(getCaCampaign);
    const selectAddons = useSelector(getSelectedInlineAddons);
    const cartOrderDetails = useSelector(getCartOrder);
    const isCaDelivery = CaPriceVariant(skuDynamicPrice, isCaTransparencyPricing);
    const selectedSkuDynamicPrice = skuDynamicPrice?.newPrices?.find?.((p) => p.id === selectedSku.id)?.prices || [];
    const isPassportAdded = useSelector(getPassportSubscriptionStatus);
    const isMobilePdp = deviceType === 'mobile';
    const caPriceTransparencyFlag = featureFlags['is-gpt-price-transparency-enabled'];
    const caPriceWithoutChargeOnGpt = skuDynamicPrice?.newPrices?.[0]?.productType !== 'FPT' && !caPriceTransparencyFlag?.standard?.withDeliveryCharges;

    useEffect(() => {
        cartOrderDetails?.recipients?.forEach((cartItem) => {
            cartItem?.cartItems?.forEach((item) => {
                if (item?.itemInfo?.lineItemType === 'PASSPORT') {
                    setPassportPrice(item?.itemInfo?.price);
                }
            });
        });
        if (!isPassportAdded) {
            setPassportPrice(0);
        }
    }, [cartOrderDetails, isPassportAdded]);

    useEffect(() => {
        const calculateTotal = () => selectAddons.reduce((sum, item) => {
            const retailPriceObj = item.prices.find((p) => p.type === 'retail');
            const salePriceObj = item.prices.find((p) => p.type === 'sale');
            const retailPrice = retailPriceObj ? retailPriceObj.value : 0;
            const salePrice = salePriceObj ? salePriceObj.value : null;
            let priceToUse = retailPrice;
            if (salePrice !== null && salePrice !== undefined) {
                // If sale price exists and is equal to retail, use sale price
                priceToUse = salePrice === retailPrice ? salePrice : retailPrice;
            }
            // If sale price is missing or null, it uses retail price by default
            return sum + priceToUse * item.quantity;
        }, 0);
        setTotalAddonPrice(calculateTotal());
    }, [selectAddons]);

    useEffect(() => {
        setSkuDynamicPrice(caCampaign);
    }, [caCampaign]);

    const isDeliverCharges = selectedSkuDynamicPrice?.some(
        (item) => Array.isArray(item.charges) && item.charges.length > 0 && Number(item.charges[0]?.amount) > 0,
    );
    const getSalePrice = (priceList) => {
        const price = (priceList || []).find((item) => (item.type === 'sale'));
        const totalPrice = caPriceWithoutChargeOnGpt ? parseFloat(price?.value) : parseFloat(price?.value) + (isDeliverCharges ? parseFloat(price.charges[0].amount) : '');
        return parseFloat(totalPrice).toFixed(2);
    };

    const getRetailPrice = (priceList) => {
        const price = (priceList || []).find((item) => (item.type === 'retail'));
        const totalPrice = caPriceWithoutChargeOnGpt ? parseFloat(price?.value) : parseFloat(price?.value) + (isDeliverCharges ? parseFloat(price.charges[0].amount) : '');
        return parseFloat(totalPrice).toFixed(2);
    };

    const retailPriceText = getRetailPrice(selectedSkuDynamicPrice);
    const salePriceText = getSalePrice(selectedSkuDynamicPrice);

    const isSalePrice = Number(salePriceText) < Number(retailPriceText);
    let addToCartPriceShow = retailPriceText;
    if (salePriceText !== retailPriceText && isSalePrice) {
        addToCartPriceShow = salePriceText;
    }
    const addToCartPrice = parseFloat(addToCartPriceShow || 0)
        + parseFloat(totalAddonPrice || 0)
        + parseFloat(passportPrice || 0)
        + parseFloat(cardIslePrice || 0);

    const { data: isPersistAddressData } = useSalesforcePersonalizedContentQuery({
        queryName: 'persistAddress',
        interactionName: `${brand?.domain} - Get Campaign - GF and GN Persist Address to CO`,
        featureFlag: ffGFAndGnPersistAddressCo && !forceGFAndGnPersistAddressCo,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    const isPersistAddress = isPersistAddressData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const isPersistAddressEnabled = forceGFAndGnPersistAddressCo || isPersistAddress?.toLowerCase?.() === 'test';

    useClickStreamCustomEventsTracking({
        salesforceResponse: isPersistAddressData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });
    /*
     * if flag is enabled and it's pmall product it will hide the ATC button and ATC will handle from Pmall Personalization button.
     * Only for PMALL products
`   */
    if (ffCurrentBrandPmallAtcOnSave
        && featureFlags['is-pmall-enabled']
        && (product?.brandId === '1030' || product?.brandId === '1033' || isPmallPersonalizationEnabled || pmallPersonalizationType)
        && selectedSku?.personalization?.personalizationAllowed
    ) {
        return <></>;
    }

    useEffect(() => {
        if (typeof isPersistAddressData !== 'undefined' && typeof window !== 'undefined' && !window.wuPersistAddressDataEventFired && isPersistAddressData) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Persist Address Checkout',
                eventLabel: isPersistAddressEnabled ? 'Variant' : 'Control',
                feature_detail: isPersistAddressEnabled ? 'Variant' : 'Control',
                feature_element: 'Persist Address Checkout',
                feature_category: 'Experiment',
                experiment_id: isPersistAddressData?.contentSF?.campaign?.campaignResponses?.[0]?.campaignId,
            };
            dispatch(trackEvent(eventParam));
            window.wuPersistAddressDataEventFired = true;
        }
        return () => {
            window.wuPersistAddressDataEventFired = false;
        };
    }, [isPersistAddressData]);

    useEffect(() => {
        if (typeof window !== 'undefined' && typeof document !== 'undefined') {
            const handleScroll = () => {
                const button = document.getElementById('pwaAddToCartBtn');
                if (button) {
                    const rect = button.getBoundingClientRect();
                    const hasDisappeared = rect.top < 0 || rect.bottom > window.innerHeight;
                    setIsOffScreen(hasDisappeared);
                }
            };
            if (isOffScreen) {
                dispatch(trackEvent({
                    eventCategory: 'Product Page',
                    eventAction: 'Scroll',
                    eventName: 'feature_interaction',
                    feature_detail: 'Scroll past Add to Cart button',
                    feature_element: 'Scroll',
                    feature_category: 'Product Page',
                }));
            }
            window.addEventListener('scroll', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
        return null;
    }, [isOffScreen]);

    return (
        featureFlags['is-pdp-v-2-enabled'] ? (
            <PDPAddToCartComponentV2
                featureFlags={featureFlags}
                isAddToCartEnabled={isAddToCartEnabled}
                selectedItemId={selectedSku.id}
                brandId={brandId}
                productSkus={productSkus}
                partNumber={partNumber}
                categoryId={location.state?.categoryId || ''}
                categoryPath={location.state?.categoryPath || ''}
                categoryName={location.state?.categoryName || ''}
                subscription={subscription}
                subscriptionDetails={subscriptionDetails}
                fullWidth={(product?.productType?.toLowerCase() === 'club')}
                disableAddToCart={disableAddToCart}
                personalizedData={personalizedData}
                isPersonalizable={product?.isPersonalizable}
                product={product}
                presentation_family={presentation_family}
                handleMiniCartModalClick={handleMiniCartModalClick}
                quantity={quantity}
                handleMobileAgeVerification={handleMobileAgeVerification}
                hasWine={hasWine}
                isPassportBundleProduct={isPassportBundleProduct || {}}
                productType={selectedSku?.productType}
                addons={selectedAddons}
                movieSelected={movieSelected?.skuSelected ? movieSelected : null}
                deliverySLA={deliverySLA}
                deliveryIndicator={deliveryIndicator}
                deliveryData={deliveryData}
                displayDeliveryCalendar={displayDeliveryCalendar ?? true}
                isPassportBundleItem={isPassportBundleItem}
                isStandAloneMovieSku={isStandAloneMovieSku}
                pmallSummaryInfo={pmallSummaryInfo}
                productDeliveryType={availability?.productDeliveryType}
                productName={name}
                productNotAvailable={productNotAvailable}
                selectedSku={selectedSku}
                skuData={sortedData}
                deviceType={deviceType}
                content={content}
                isPdpRedesignEnabled={isPdpRedesignEnabled}
                showAddToCart={showAddToCart}
                showDeliveryField={showDeliveryField}
                showProductTitleTop={showProductTitleTop}
                isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
                isPdpRedesignSkuEnabled={isPdpRedesignSkuEnabled}
                addToCartButtonRedesign={addToCartButtonRedesign}
                isPlaRedesignEnabled={isPlaRedesignEnabled}
                isPersistAddressEnabled={isPersistAddressEnabled}
                addToCartPriceShow={addToCartPriceShow}
                isCaDelivery={isCaDelivery}
                isDeliverCharges={isDeliverCharges && skuDynamicPrice?.newPrices?.[0]?.productType === 'FPT'}
                setProductQuantity={setProductQuantity}
                addToCartPrice={parseFloat(addToCartPrice)?.toFixed(2)}
                isMobilePdp={isMobilePdp}
                stickyAtcEnabled={stickyAtcEnabled}
                stickyAtcRequiresSavedZip={stickyAtcRequiresSavedZip}
            />
        ) : (
            <PDPAddToCartComponent
                validVaseConfiguratorSku={validVaseConfiguratorSku}
                featureFlags={featureFlags}
                isAddToCartEnabled={isAddToCartEnabled}
                selectedItemId={selectedSku.id}
                brandId={brandId}
                productSkus={productSkus}
                partNumber={partNumber}
                categoryId={location.state?.categoryId || ''}
                categoryPath={location.state?.categoryPath || ''}
                categoryName={location.state?.categoryName || ''}
                subscription={subscription}
                subscriptionDetails={subscriptionDetails}
                fullWidth={(product?.productType?.toLowerCase() === 'club')}
                disableAddToCart={disableAddToCart}
                personalizedData={personalizedData}
                isPersonalizable={product?.isPersonalizable}
                product={product}
                presentation_family={presentation_family}
                handleMiniCartModalClick={handleMiniCartModalClick}
                quantity={quantity}
                handleMobileAgeVerification={handleMobileAgeVerification}
                hasWine={hasWine}
                isPassportBundleProduct={isPassportBundleProduct || {}}
                productType={selectedSku?.productType}
                addons={selectedAddons}
                movieSelected={movieSelected?.skuSelected ? movieSelected : null}
                deliverySLA={deliverySLA}
                deliveryIndicator={deliveryIndicator}
                deliveryData={deliveryData}
                displayDeliveryCalendar={displayDeliveryCalendar ?? true}
                isPassportBundleItem={isPassportBundleItem}
                isStandAloneMovieSku={isStandAloneMovieSku}
                pmallSummaryInfo={pmallSummaryInfo}
                productDeliveryType={availability?.productDeliveryType}
                productName={name}
                productNotAvailable={productNotAvailable}
                selectedSku={selectedSku}
                skuData={sortedData}
                deviceType={deviceType}
                content={content}
                isPdpRedesignEnabled={isPdpRedesignEnabled}
                showAddToCart={showAddToCart}
                showDeliveryField={showDeliveryField}
                showProductTitleTop={showProductTitleTop}
                isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
                isPdpRedesignSkuEnabled={isPdpRedesignSkuEnabled}
                addToCartButtonRedesign={addToCartButtonRedesign}
                isPlaRedesignEnabled={isPlaRedesignEnabled}
                isPersistAddressEnabled={isPersistAddressEnabled}
                addToCartPriceShow={addToCartPriceShow}
                isCaDelivery={isCaDelivery}
                isDeliverCharges={isDeliverCharges && skuDynamicPrice?.newPrices?.[0]?.productType === 'FPT'}
                setProductQuantity={setProductQuantity}
                addToCartPrice={parseFloat(addToCartPrice)?.toFixed(2)}
                isMobilePdp={isMobilePdp}
                stickyAtcEnabled={stickyAtcEnabled}
                stickyAtcRequiresSavedZip={stickyAtcRequiresSavedZip}
            />
        )
    );
};

PDPAddToCart.propTypes = {
    movieSelected: object.isRequired,
    product: object.isRequired,
    deliveryData: object.isRequired,
    partNumber: string,
    hasWine: bool.isRequired,
    presentation_family: string.isRequired,
    selectedSku: object.isRequired,
    sortedData: array.isRequired,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    location: object.isRequired,
    isPassportBundleItem: bool,
    displayDeliveryCalendar: bool,
    disableAddToCart: bool,
    personalizedData: shape({
        personalizationType: string,
        personalizationLines: arrayOf({
            perAttributeValue: string,
        }),
    }),

    isAddToCartEnabled: bool.isRequired,
    selectedAddons: arrayOf(string),
    handleMiniCartModalClick: func.isRequired,
    quantity: string,
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    handleMobileAgeVerification: func,
    deviceType: string,
    deliverySLA: string,
    isStandAloneMovieSku: bool,
    deliveryIndicator: string.isRequired,
    productNotAvailable: shape({
        product_not_available_text: string.isRequired,
        cta_text: string.isRequired,
    }),
    pmallSummaryInfo: shape({
        customizations: arrayOf(
            shape({
                refnum: string,
                description: string,
                'preview-url': string,
            }),
        ),
    }).isRequired,
    validVaseConfiguratorSku: bool.isRequired,
    content: object,
    isPdpRedesignEnabled: bool,
    showAddToCart: bool,
    showDeliveryField: bool,
    showProductTitleTop: bool,
    isPdpRedesignShortEnabled: bool,
    isPdpRedesignSkuEnabled: bool,
    addToCartButtonRedesign: bool,
    isPlaRedesignEnabled: bool,
    setProductQuantity: func,
    stickyAtcEnabled: bool,
    stickyAtcRequiresSavedZip: bool,
};

PDPAddToCart.defaultProps = {
    partNumber: null,
    isStandAloneMovieSku: false,
    subscriptionDetails: {},
    subscription: {},
    isPassportBundleItem: false,
    disableAddToCart: false,
    personalizedData: {},
    displayDeliveryCalendar: true,
    selectedAddons: [],
    quantity: '',
    isPassportBundleProduct: {},
    handleMobileAgeVerification: () => { },
    deviceType: '',
    deliverySLA: '',
    productNotAvailable: {},
    content: {},
    isPdpRedesignEnabled: false,
    showAddToCart: true,
    showDeliveryField: true,
    showProductTitleTop: false,
    isPdpRedesignShortEnabled: false,
    isPdpRedesignSkuEnabled: false,
    addToCartButtonRedesign: false,
    isPlaRedesignEnabled: false,
    setProductQuantity: () => { },
    stickyAtcEnabled: false,
    stickyAtcRequiresSavedZip: false,
};

export default (PDPAddToCart);
